import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/apiClient";
import { Sparkles, Eye, Lightbulb, MessageCircle, ArrowRight, Check, Moon, TrendingUp, Target } from "lucide-react";
import { toast } from "sonner";

const Block = ({ i, icon: Icon, label, children }) => (
  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 * i, duration: 0.5 }}>
    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-primary mb-2">
      <Icon className="w-3.5 h-3.5" /> {label}
    </div>
    {children}
  </motion.div>
);

export default function Mirror({ open, mirror, sessionId, onClose }) {
  const navigate = useNavigate();
  const [confirmed, setConfirmed] = useState(false);
  if (!mirror) return null;

  const wentWell = mirror.what_went_well || [];
  const explore = mirror.moments_to_explore || [];
  const tryInstead = mirror.try_instead || [];
  const dims = mirror.dimensions || [];
  const obs = mirror.coachs_observation;
  const presc = mirror.prescription;
  const inst = presc?.installation || {};

  const respond = async (response) => {
    try {
      await api.post(`/practice/sessions/${sessionId}/confirm`, { response });
      setConfirmed(true);
      if (response === "confirm") toast.success("Held. This becomes your focus.");
    } catch { toast.error("Something went wrong."); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto glass" data-testid="mirror-dialog">
        <VisuallyHidden><DialogTitle>Your reflection</DialogTitle><DialogDescription>What your coach noticed in your practice conversation.</DialogDescription></VisuallyHidden>

        <div className="text-center pt-1 pb-1">
          <div className="text-xs uppercase tracking-[0.25em] text-primary mb-1">Your reflection</div>
          <div className="font-serif text-5xl text-primary" data-testid="mirror-score">{mirror.score}</div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">presence this session</div>
          {mirror.headline && <p className="font-serif text-xl leading-snug mt-4 px-2" data-testid="mirror-headline">{mirror.headline}</p>}
          {mirror.skill_targeted && (
            <div className="inline-block mt-3 text-xs px-3 py-1 rounded-full border border-border text-muted-foreground" data-testid="mirror-skill">
              Worked on · {mirror.skill_targeted}
            </div>
          )}
        </div>

        <div className="space-y-6 py-4">
          {mirror.progress && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-secondary/40 bg-secondary/[0.06] p-5" data-testid="mirror-progress">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-secondary mb-2"><TrendingUp className="w-3.5 h-3.5" /> Your change</div>
              {typeof mirror.progress.previous_score === "number" && (
                <div className="flex items-center gap-3 mb-3">
                  <div className="text-center"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Before</div><div className="font-serif text-3xl text-muted-foreground">{mirror.progress.previous_score}</div></div>
                  <ArrowRight className="w-4 h-4 text-secondary" />
                  <div className="text-center"><div className="text-[10px] uppercase tracking-widest text-secondary">Now</div><div className="font-serif text-3xl text-secondary">{mirror.progress.this_score ?? mirror.score}</div></div>
                </div>
              )}
              <p className="text-sm leading-relaxed">{mirror.progress.change_note}</p>
            </motion.div>
          )}

          {Array.isArray(mirror.marker_results) && mirror.marker_results.length > 0 && (
            <Block i={0} icon={Target} label="What we watched for">
              <div className="space-y-2">
                {mirror.marker_results.map((r, k) => (
                  <div key={k} className="flex items-start gap-2 text-sm" data-testid={`mirror-marker-${k}`}>
                    <span className={`mt-0.5 shrink-0 ${r.met === "yes" ? "text-teal-400" : r.met === "partial" ? "text-amber-400" : "text-muted-foreground"}`}>
                      {r.met === "yes" ? "●" : r.met === "partial" ? "◐" : "○"}
                    </span>
                    <span className="text-muted-foreground">{r.marker}</span>
                  </div>
                ))}
              </div>
            </Block>
          )}

          {wentWell.length > 0 && (
            <Block i={0} icon={Sparkles} label="What went well">
              <div className="space-y-3">
                {wentWell.map((w, k) => (
                  <div key={k}>
                    <p className="text-sm leading-relaxed">"{w.moment}"</p>
                    <p className="text-sm text-muted-foreground">{w.why}</p>
                  </div>
                ))}
              </div>
            </Block>
          )}

          {explore.length > 0 && (
            <Block i={1} icon={Eye} label="Moments to explore">
              <div className="space-y-3">
                {explore.map((e, k) => (
                  <div key={k}>
                    <p className="text-sm leading-relaxed">"{e.moment}"</p>
                    <p className="text-sm text-muted-foreground italic">{e.observation}</p>
                  </div>
                ))}
              </div>
            </Block>
          )}

          {tryInstead.length > 0 && (
            <Block i={2} icon={Lightbulb} label="What you could try instead">
              <div className="space-y-3">
                {tryInstead.map((t, k) => (
                  <div key={k} className="rounded-xl border border-border p-3">
                    <p className="text-sm"><span className="text-primary font-medium">Try:</span> {t.try}</p>
                    <p className="text-xs text-muted-foreground mt-1">{t.difference}</p>
                  </div>
                ))}
              </div>
            </Block>
          )}

          {dims.length > 0 && (
            <Block i={3} icon={MessageCircle} label="What mattered here">
              <div className="space-y-3">
                {dims.map((d, k) => (
                  <div key={k} data-testid={`mirror-dim-${k}`}>
                    <div className="flex justify-between text-sm mb-1"><span>{d.name}</span><span className="text-muted-foreground">{d.score}</span></div>
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                      <motion.div initial={{ width: 0 }} animate={{ width: `${d.score}%` }} transition={{ duration: 0.9, delay: 0.1 * k }} className="h-full rounded-full bg-gradient-to-r from-coral-400 to-secondary" />
                    </div>
                    {d.note && <p className="text-xs text-muted-foreground mt-1">{d.note}</p>}
                  </div>
                ))}
              </div>
            </Block>
          )}

          {obs && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="rounded-2xl border border-border p-5 bg-primary/5" data-testid="mirror-observation">
              <div className="text-xs uppercase tracking-[0.18em] text-primary mb-2">A coach's observation</div>
              <p className="font-serif text-lg leading-snug mb-4">{obs}</p>
              {!confirmed ? (
                <div className="flex gap-2">
                  <Button size="sm" className="rounded-full flex-1" onClick={() => respond("confirm")} data-testid="mirror-confirm">That feels familiar</Button>
                  <Button size="sm" variant="outline" className="rounded-full flex-1" onClick={() => respond("reject")} data-testid="mirror-reject">Not quite</Button>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-primary"><Check className="w-4 h-4" /> Thank you for being honest with yourself.</div>
              )}
            </motion.div>
          )}

          {presc ? (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
              className="rounded-2xl overflow-hidden relative border border-border" data-testid="mirror-prescription">
              {inst.cover && <img src={inst.cover} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />}
              <div className="relative p-5">
                <div className="text-xs uppercase tracking-[0.18em] text-primary mb-2 flex items-center gap-1.5"><Moon className="w-3.5 h-3.5" /> Something for tonight</div>
                <p className="text-sm leading-relaxed mb-3">{presc.coach_message}</p>
                {presc.duration_min && <p className="text-xs text-muted-foreground mb-4">{presc.duration_min} min · designed for you</p>}
                <div className="flex gap-2">
                  {inst.id && (
                    <Button size="sm" className="rounded-full flex-1" onClick={() => navigate(`/inner/${inst.id}`)} data-testid="mirror-listen">
                      Listen now <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="rounded-full flex-1" onClick={() => { toast.success("Saved for tonight."); onClose(); navigate("/app"); }} data-testid="mirror-save">
                    Save for tonight
                  </Button>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="rounded-2xl border border-border p-5 text-center" data-testid="mirror-no-prescription">
              <p className="text-sm text-muted-foreground">Nothing to prescribe tonight. Just sit with how that felt — that's enough.</p>
            </motion.div>
          )}
        </div>

        <div className="flex gap-3">
          <Button variant="outline" className="flex-1 rounded-full" onClick={() => navigate("/app")} data-testid="mirror-home">Home</Button>
          <Button className="flex-1 rounded-full" onClick={() => { onClose(); navigate("/practice"); }} data-testid="mirror-again">Practise again</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
